import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { X } from 'lucide-react';
import { Sixy } from './Sixy';
import { haptic } from '../utils/haptic';
import { playSound } from '../utils/sound';
import { Z } from '../utils/zIndex';

/**
 * Sixy Concierge — intervenções proativas educadas da Sixy.
 *
 * Conceito:
 *  - Toast bottom-anchored com orb + mensagem + opcional CTA
 *  - Orb dispara entrada com olhinhos quando aparece (vide SixyOrb entrance)
 *  - Auto-dismiss em 8s, dismissable manual
 *  - Sistema de cooldown global pra não ser chata:
 *    • Máx 1 ativo por vez
 *    • Cooldown de 90s entre intervenções
 *    • Cada trigger key tem cooldown próprio em localStorage
 *    • Respeita "dismissed by user" pra não repetir
 *
 * Uso:
 *  1. Envolver app com <ConciergeProvider>
 *  2. Em qualquer lugar: const { show } = useConcierge();
 *     show({ key: 'welcome_day', message: 'Bom dia!', cooldownMs: 24h })
 *  3. Triggers comuns wired no useAutoConcierge hook
 */

interface ConciergeMessage {
  /** Identificador único do tipo de mensagem — usado pra cooldown */
  key: string;
  /** Texto da mensagem */
  message: string;
  /** Mood da Sixy ao aparecer */
  mood?: 'happy' | 'thinking' | 'excited';
  /** CTA opcional */
  cta?: {
    label: string;
    onClick: () => void;
  };
  /** Cooldown entre apresentações desta key. Default 24h. */
  cooldownMs?: number;
  /** Auto-dismiss em ms. Default 8000. */
  autoDismissMs?: number;
}

interface ConciergeContextValue {
  /** Mostra uma mensagem. Retorna true se mostrou, false se em cooldown. */
  show: (msg: ConciergeMessage) => boolean;
  /** Limpa o concierge atual. */
  dismiss: () => void;
}

const ConciergeContext = createContext<ConciergeContextValue | null>(null);

const STORAGE_PREFIX = 'sixpass.concierge.';
const GLOBAL_COOLDOWN_MS = 90 * 1000; // 90s entre intervenções
const DEFAULT_KEY_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h
const DEFAULT_AUTO_DISMISS_MS = 8000;

export function ConciergeProvider({ children }: { children: ReactNode }) {
  const [current, setCurrent] = useState<ConciergeMessage | null>(null);
  const lastShownAtRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const currentRef = useRef<ConciergeMessage | null>(null);
  // Mantém ref sincronizada com state pra usar dentro de callbacks estáveis
  useEffect(() => {
    currentRef.current = current;
  }, [current]);

  // dismiss é estável: sem dependências, lê do timerRef
  const dismiss = useCallback(() => {
    setCurrent(null);
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // show é ESTÁVEL — sem deps. Lê estado via refs. Crucial: se ele recriasse
  // a cada render, consumidores com useEffect dependendo dele teriam timers
  // limpos infinitamente, e nada do concierge dispararia.
  const show = useCallback((msg: ConciergeMessage): boolean => {
    const now = Date.now();
    // Global cooldown — evita spam mesmo entre mensagens diferentes
    if (now - lastShownAtRef.current < GLOBAL_COOLDOWN_MS) return false;
    // Key-specific cooldown
    try {
      const lastRaw = localStorage.getItem(STORAGE_PREFIX + msg.key);
      if (lastRaw) {
        const lastTime = parseInt(lastRaw, 10);
        const cooldown = msg.cooldownMs ?? DEFAULT_KEY_COOLDOWN_MS;
        if (!isNaN(lastTime) && now - lastTime < cooldown) return false;
      }
    } catch {
      // localStorage pode falhar em private mode — fail-open (mostra mensagem)
    }

    // Tem outra ativa? Cancela timer + substitui
    if (currentRef.current && timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Mark as shown
    lastShownAtRef.current = now;
    try {
      localStorage.setItem(STORAGE_PREFIX + msg.key, String(now));
    } catch {
      // localStorage pode falhar em private mode — ignora
    }

    setCurrent(msg);
    // Auto-dismiss
    const autoMs = msg.autoDismissMs ?? DEFAULT_AUTO_DISMISS_MS;
    timerRef.current = window.setTimeout(() => {
      setCurrent(null);
      timerRef.current = null;
    }, autoMs);
    return true;
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, []);

  // value é estável porque show e dismiss são estáveis
  const value = useMemo<ConciergeContextValue>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ConciergeContext.Provider value={value}>
      {children}
      <ConciergeToast message={current} onDismiss={dismiss} />
    </ConciergeContext.Provider>
  );
}

export function useConcierge(): ConciergeContextValue {
  const ctx = useContext(ConciergeContext);
  if (!ctx) {
    // Fail-soft em vez de throw — concierge é opcional
    return {
      show: () => false,
      dismiss: () => {},
    };
  }
  return ctx;
}

/**
 * Toast visual do Concierge — slide do topo, orb à esquerda + texto + dismiss.
 * Top-anchored (não bottom) porque o bottom já tem tab bar + FAB.
 */
function ConciergeToast({
  message,
  onDismiss,
}: {
  message: ConciergeMessage | null;
  onDismiss: () => void;
}) {
  useEffect(() => {
    if (!message) return;
    haptic('light');
    playSound('notif');
  }, [message]);

  return (
    <AnimatePresence>
      {message && (
        <motion.div
          initial={{ y: -120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 26 }}
          className="fixed left-3 right-3 flex justify-center pointer-events-none"
          style={{
            top: 'calc(var(--safe-top, 0px) + 0.75rem)',
            zIndex: Z.CONCIERGE,
          }}
        >
          <div
            className="pointer-events-auto w-full max-w-md rounded-2xl border border-white/15 bg-[#0a0a14]/95 backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.6)] overflow-hidden"
            style={{
              boxShadow:
                '0 20px 50px rgba(0,0,0,0.6), 0 0 0 1px rgba(168,85,247,0.15), 0 0 40px rgba(168,85,247,0.2)',
            }}
          >
            <div className="flex items-start gap-3 px-3 py-3">
              <div className="shrink-0 pt-0.5">
                <Sixy
                  form="cloud"
                  entry="fly-in"
                  flyFrom={45}
                  flyDistance={120}
                  size="md"
                  mood={message.mood || 'happy'}
                  talking
                />
              </div>
              <div className="min-w-0 flex-1 py-1">
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-purple-300 mb-1">
                  Sixy
                </p>
                <p className="text-sm font-medium text-white leading-snug">
                  {message.message}
                </p>
                {message.cta && (
                  <button
                    type="button"
                    onClick={() => {
                      haptic('medium');
                      message.cta?.onClick();
                      onDismiss();
                    }}
                    className="mt-2 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-cyan-500 to-purple-500 px-3 py-1 text-[11px] font-bold text-white transition-transform active:scale-95"
                  >
                    {message.cta.label} →
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  haptic('light');
                  onDismiss();
                }}
                aria-label="Dispensar"
                className="shrink-0 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
              >
                <X size={14} aria-hidden="true" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
